// Phase 9 R3 — Inventory search hook for the parts picker.
// Server-side ilike across 7 fields. Active items only. Top 20 by name.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { InventoryItem } from './useInventoryItems';

function escapeIlike(input: string): string {
  return input.replace(/[,%_]/g, (m) => `\\${m}`);
}

export function useInventorySearch(
  shopId: string | undefined,
  query: string,
  enabled: boolean = true,
) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: ['inventory-search', shopId, trimmed, true] as const,
    enabled: !!shopId && enabled && trimmed.length > 0,
    queryFn: async (): Promise<InventoryItem[]> => {
      if (!shopId || trimmed.length === 0) return [];
      const pattern = `%${escapeIlike(trimmed)}%`;
      const orExpr = [
        `part_number.ilike.${pattern}`,
        `part_number_normalized.ilike.${pattern}`,
        `brand.ilike.${pattern}`,
        `name.ilike.${pattern}`,
        `supplier.ilike.${pattern}`,
        `supplier_part_number.ilike.${pattern}`,
      ].join(',');

      const { data, error } = await supabase
        .from('inventory_items')
        .select('*')
        .eq('shop_id', shopId)
        .eq('is_active', true)
        .or(orExpr)
        .order('name', { ascending: true, nullsFirst: false })
        .limit(20);

      if (error) throw error;
      return (data ?? []) as InventoryItem[];
    },
    staleTime: 15_000,
  });
}
